import React, { useState, useEffect } from "react";
import { Button } from "antd";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaInstagram } from "react-icons/fa";

const SLIDE_INTERVAL = 4200;

// Probes /sections/orchester/1.png|jpg, 2.png|jpg, ... until neither exists.
// Adding more images (any mix of .png/.jpg) is enough — no code change needed.
function OrchestraSlideshow() {
  const [images, setImages] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const tryLoad = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = src;
      });
    const probe = async () => {
      const srcs = [];
      for (let i = 1; i <= 30; i++) {
        const pngSrc = `/sections/orchester/${i}.png`;
        const jpgSrc = `/sections/orchester/${i}.jpg`;
        if (await tryLoad(pngSrc)) {
          srcs.push(pngSrc);
        } else if (await tryLoad(jpgSrc)) {
          srcs.push(jpgSrc);
        } else {
          break;
        }
      }
      if (!cancelled) setImages(srcs);
    };
    probe();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(
      () => setActiveIdx((i) => (i + 1) % images.length),
      SLIDE_INTERVAL,
    );
    return () => clearInterval(timer);
  }, [images.length]);

  if (!images.length) return null;

  return (
    <div className="orchestraSlideshow">
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`Sax'emble Orchester – Bild ${i + 1}`}
          className={i === activeIdx ? "active" : ""}
        />
      ))}
    </div>
  );
}

function AppAbout() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div id="about" className="aboutBlock">
      <div className="container-fluid">
        <motion.div
          className="titleHolder"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          ref={ref}
        >
          <h2>Das Orchester</h2>
        </motion.div>

        <div className="twoCol">
          <motion.div
            className="textCol"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.1 }}
          >
            <p>
              sax'emble entstand aus dem Saxophonorchester VARIOSAX. Dieses
              wurde im Herbst 2004 von den Schulmusikern Annette Kutzer und
              Albert Loritz als Schulorchester des Albert-Schweitzer-Gymnasiums
              in Neckarsulm gegründet. Durch die Pensionierung der beiden Leiter
              und ihren Abschied vom Albert-Schweitzer-Gymnasium wurde das
              Orchester ab September 2017 in das Projektorchester VARIOSAX –
              Saxophonorchester Kloster Schöntal umgewandelt.
            </p>
            <p>
              Leider verstarb Albert Loritz nach kurzer, schwerer Krankheit im
              Jahr 2018, sodass Annette Kutzer das Orchester fortan allein
              weiterführte. Gegen Weihnachten 2022 beschloss auch sie, sich in
              den wohlverdienten Ruhestand zu verabschieden, und erklärte das
              Projekt VARIOSAX für beendet.
            </p>
            <p>
              Beim Abschiedsessen bekundeten jedoch mehrere Spielerinnen und
              Spieler den Wunsch, gerne weitermachen zu wollen – und so
              beschloss die Mehrheit: Ja, es geht weiter! Nach einem Aufruf in
              den umliegenden Gemeinden kamen mehrere neue Mitspielende hinzu.
              Heute zählen wir etwa 25 Spielende. Zum Neustart beschlossen wir,
              einen neuen Namen zu finden: sax'emble – die Kurzform für das
              sax'emble Saxophonorchester Kloster Schöntal.
            </p>
            <p>
              Die Proben finden einmal monatlich im Kloster Schöntal statt. Das
              eingeübte, abwechslungsreiche Programm präsentieren wir bei
              unseren halbjährlich stattfindenden Konzerten.
            </p>

            <Button
              type="primary"
              size="large"
              href="https://www.instagram.com/saxemble_schoental"
              target="_blank"
              rel="noopener noreferrer"
              className="instagramBtn"
              icon={
                <FaInstagram
                  style={{ marginRight: 6, verticalAlign: "middle" }}
                />
              }
            >
              Schau auf Instagram vorbei!
            </Button>
          </motion.div>

          <motion.div
            className="mediaHolder"
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.25 }}
          >
            <OrchestraSlideshow />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default AppAbout;
